import { IT_INT } from '@slims/shared/constants';
import * as React from 'react';

export interface TimeBandBarProps {
	/** Total monthly kWh to split across the F1/F2/F3 bands (0 → em-dash labels). */
	totalKwh: number;
	/** Band shares; defaults to ARERA/TERNA statistical profile F1 43% / F2 27% / F3 30%. */
	f1Pct?: number;
	f2Pct?: number;
	formatNumber?: (n: number) => string;
}

/**
 * Stacked horizontal bar of the ARERA F1/F2/F3 time bands with kWh labels.
 *
 * Grid HTML, non un unico `<svg viewBox>` scalato: un viewBox fisso forzato a
 * riempire una card larga quanto capita richiede `preserveAspectRatio="none"`,
 * che stira X e Y in proporzioni diverse — testo e angoli arrotondati escono
 * distorti. Tre righe di celle CSS Grid con le stesse `gridTemplateColumns`
 * (le percentuali F1/F2/F3) restano allineate tra loro senza scalare nulla:
 * il testo resta nitido a qualunque larghezza di card.
 */
const TimeBandBar = ({
	totalKwh,
	f1Pct = 0.43,
	f2Pct = 0.27,
	formatNumber = (n) => n.toLocaleString('it-IT', IT_INT),
}: TimeBandBarProps) => {
	const f3Pct = 1 - f1Pct - f2Pct;
	const f1kwh = Math.round(totalKwh * f1Pct);
	const f2kwh = Math.round(totalKwh * f2Pct);
	const f3kwh = Math.round(totalKwh * f3Pct);
	const gridTemplateColumns = `${f1Pct * 100}% ${f2Pct * 100}% ${f3Pct * 100}%`;

	return (
		<div
			className="mb-3 font-mono"
			// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- barra CSS a più celle, senza sorgente raster: role + aria-label è il pattern accessibile
			role="img"
			aria-label={`Distribuzione fasce orarie: F1 ${Math.round(f1Pct * 100)}%, F2 ${Math.round(f2Pct * 100)}%, F3 ${Math.round(f3Pct * 100)}%`}
		>
			{/* Labels above bars — colore fisso come nel mock (letterale, non dal
			    tema): stessa ragione dei colori di serie nei grafici, vedi
			    `--color-series-*` in tokens.css. */}
			<div className="grid gap-1 text-[12px]" style={{ gridTemplateColumns }}>
				<span className="truncate" style={{ color: 'var(--color-series-2)' }}>
					F1 Punta (lun-ven 8-19)
				</span>
				<span className="truncate text-right" style={{ color: 'var(--color-series-1)' }}>
					F2
				</span>
				<span className="truncate text-right" style={{ color: 'var(--color-series-4)' }}>
					F3
				</span>
			</div>
			{/* Bars */}
			<div className="mt-1 grid gap-0" style={{ gridTemplateColumns }}>
				<div
					className="flex h-8 items-center justify-center truncate rounded-l-[4px] text-[13px] font-bold text-black"
					style={{ background: 'rgba(245,158,11,0.6)' }}
				>
					F1 {Math.round(f1Pct * 100)}%
				</div>
				<div
					className="flex h-8 items-center justify-center truncate text-[13px] font-bold text-black"
					style={{ background: 'color-mix(in srgb, var(--color-accent) 50%, transparent)' }}
				>
					F2 {Math.round(f2Pct * 100)}%
				</div>
				<div
					className="flex h-8 items-center justify-center truncate rounded-r-[4px] text-[13px] text-white"
					style={{ background: 'rgba(16,185,129,0.5)' }}
				>
					F3
				</div>
			</div>
			{/* kWh below bars */}
			<div className="mt-1 grid gap-1 text-[12px] text-text-muted" style={{ gridTemplateColumns }}>
				<span className="truncate">{f1kwh > 0 ? `${formatNumber(f1kwh)} kWh` : '- kWh'}</span>
				<span className="truncate">{f2kwh > 0 ? `${formatNumber(f2kwh)} kWh` : '-'}</span>
				<span className="truncate">{f3kwh > 0 ? `${formatNumber(f3kwh)} kWh` : '-'}</span>
			</div>
		</div>
	);
};
TimeBandBar.displayName = 'TimeBandBar';

export { TimeBandBar };
